import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DownloadIcon,
  EditIcon,
} from '@chakra-ui/icons';
import {
  Box,
  Button,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useCallback, useMemo, useState } from 'react';
import { downloadResourceMatrixFile } from '../../services/resource-matrix-search/downloadResMatrixFile';
import { updateResourceMatrix } from '../../services/resource-matrix-search/updateResMatrix';
import { SearchResults } from '../../types';

interface Props {
  searchResults: SearchResults;
  setSearchResults: React.Dispatch<
    React.SetStateAction<SearchResults | null>
  >;
}

export function SearchResultsTable({
  searchResults,
  setSearchResults,
}: Props) {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [editValue, setEditValue] = useState<string | null>('');
  const [editRow, setEditRow] = useState<string>('');
  const [editColumn, setEditColumn] = useState<string>('');
  const [uuid, setUuid] = useState<string | null>('');
  const [expirationDate, setExpirationDate] = useState('');
  const [dataUpdating,setDataUpdating] = useState(false);
  // Pagination variables
  const itemsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);

  // Logic to handle pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  //Downloading vairables
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadKey, setDownloadKey] = useState('');

  const currentItems = useMemo(() => {
    const items = Object.keys(searchResults['phone/fax']).slice(
      indexOfFirstItem,
      indexOfLastItem
    );
    return items;
  }, [searchResults, indexOfFirstItem, indexOfLastItem]);

  const handleCellClick = useCallback(
    (
      row: string,
      column: string,
      value: string | null,
      uuid: string | null,
      expirationDate?: string | null
    ) => {
      setEditRow(row);
      setEditColumn(column);
      setEditValue(value);
      setExpirationDate(
        expirationDate ? expirationDate.toString() : ''
      );
      setUuid(uuid ? uuid.toString() : '');
      onOpen();
    },
    [onOpen]
  );

  const handleCellChange = useCallback(async () => {
    setDataUpdating(true);
    const updatedResults = { ...searchResults };
    updatedResults[editColumn][editRow] = editValue;

    try {
      if (editColumn && editValue && uuid) {
        let data;

        if (editColumn === 'office notes:') {
          // Update office notes and notes expiration separately
          const officeNotesUpdatePromise = updateResourceMatrix(
            'office notes:',
            uuid,
            editValue
          );
          const expirationUpdatePromise = updateResourceMatrix(
            'NotesExpiration',
            uuid,
            expirationDate
          );

          const [officeNotesUpdate, expirationUpdate] =
            await Promise.all([
              officeNotesUpdatePromise,
              expirationUpdatePromise,
            ]);
          setDataUpdating(officeNotesUpdate.loading && expirationUpdate.loading);
          if (
            officeNotesUpdate.data === 'Update Successful' &&
            expirationUpdate.data === 'Update Successful'
          ) {
            toast({
              title: 'Office Notes Updated',
              description:
                'Office notes and expiration updated successfully.',
              status: 'success',
              duration: 5000,
              isClosable: true,
            });
            updatedResults['NotesExpiration'][editRow] =
              expirationDate;

            setSearchResults(updatedResults);
            onClose();
            setEditRow('');
            setEditColumn('');
            setEditValue('');
            setExpirationDate('');
            setUuid('');
          } else {
            setDataUpdating(false);
            throw new Error(
              'Failed to update office notes or expiration.'
            );
          }
        } else if (editColumn === 'daily alert') {
          const dailyAlertsUpdatePromise = await updateResourceMatrix(
            'daily alert',
            uuid,
            editValue
          );
          const alertExpirationUpdatePromise =
            await updateResourceMatrix(
              'AlertExpiration',
              uuid,
              expirationDate
            );
          const [dailyAlertsUpdate, alertExpirationUpdate] =
            await Promise.all([
              dailyAlertsUpdatePromise,
              alertExpirationUpdatePromise,
            ]);
          setDataUpdating(dailyAlertsUpdate.loading && alertExpirationUpdate.loading)
          if (
            dailyAlertsUpdate.data === 'Update Successful' &&
            alertExpirationUpdate.data === 'Update Successful'
          ) {
            toast({
              title: 'Daily Alerts Updated',
              description:
                'Daily alerts and expiration updated successfully.',
              status: 'success',
              duration: 5000,
              isClosable: true,
            });
            updatedResults['AlertExpiration'][editRow] =
              expirationDate;
            setSearchResults(updatedResults);
            onClose();
            setEditRow('');
            setEditColumn('');
            setEditValue('');
            setExpirationDate('');
            setUuid('');
          } else {
            setDataUpdating(false)
            throw new Error(
              'Failed to update daily alerts or expiration.'
            );
          }
        } else {
          // Update other columns separately
          const response = await updateResourceMatrix(
            editColumn,
            uuid,
            editValue
          );
          data = response.data;
          setDataUpdating(response.loading)
        }

        if (!data || data === 'Update Successful') {
          toast({
            title: 'Update Successful',
            description: 'Resource matrix updated successfully.',
            status: 'success',
            duration: 5000,
            isClosable: true,
          });
          setSearchResults(updatedResults);
          onClose();
          setEditRow('');
          setEditColumn('');
          setEditValue('');
          setExpirationDate('');
          setUuid('');
        } else {
          setDataUpdating(false)
          throw new Error('Failed to update resource matrix.');
        }
      }
    } catch (error) {
      setDataUpdating(false)
      toast({
        title: 'Update Failed',
        description: 'Failed to update resource matrix.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [
    searchResults,
    editColumn,
    editRow,
    editValue,
    uuid,
    expirationDate,
    toast,
    setSearchResults,
    onClose,
  ]);

  const handleDownload = async (filename: string, key:string) => {
    if (isDownloading) {
      return;
    }
    setIsDownloading(true);
    setDownloadKey(key)
    try {
      const { url, loading } = await downloadResourceMatrixFile(
        filename
      );

      if (!loading && url) {
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = filename;
        anchor.target = '_blank'; // Open in a new tab
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);

        toast({
          title: 'Download Started',
          description: `Downloading ${filename} in a new tab.`,
          status: 'info',
          duration: 5000,
          isClosable: true,
        });
      } else {
        console.error('Failed to get file URL');
        toast({
          title: 'Download Failed',
          description: 'Failed to get the file URL.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Failed to download file:', error);
      toast({
        title: 'Download Failed',
        description: 'An error occurred while downloading the file.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsDownloading(false);
      setDownloadKey('')
    }
  };

  const hasResults = Object.keys(searchResults).length > 0;

  return (
    <Box>
      {hasResults ? (
        <>
          <Table
            display={'block'}
            maxWidth={'100%'}
            size={'sm'}
            whiteSpace={'nowrap'}
            variant={'striped'}
            colorScheme="gray"
            paddingX={4}
            overflowX={'scroll'}
            paddingBottom={4}
          >
            <Thead>
              <Tr>
                <Th>Daily Alerts</Th>
                <Th>Office Notes</Th>
                <Th>File</Th>
                {Object.keys(searchResults).map((columnName) => {
                  if (
                    columnName !== 'UUID' &&
                    columnName !== 'AlertExpiration' &&
                    columnName !== 'NotesExpiration' &&
                    columnName !== 'office notes:' &&
                    columnName !== 'daily alert' &&
                    columnName !== 'sheet' &&
                    columnName !== 'phone' &&
                    columnName !== 'URL' &&
                    columnName !== 'name' &&
                    columnName !== 'file' &&
                    columnName !== 'practice'
                  ) {
                    return <Th key={columnName}>{columnName}</Th>;
                  }
                  return null; // Exclude UUID from headers
                })}
              </Tr>
            </Thead>
            <Tbody>
              {currentItems.map((key) => {
                const fileName = searchResults['file'][key];
                const rowData: Record<string, string | null> = {};
                const uuid = searchResults['UUID'][key];

                // Merge alert expiration into daily alert and notes expiration into office notes
                const dailyAlertValue =
                  searchResults['daily alert'][key];
                const alertExpirationValue =
                  searchResults['AlertExpiration'][key];
                const officeNotesValue =
                  searchResults['office notes:'][key];
                const notesExpirationValue =
                  searchResults['NotesExpiration'][key];
                return (
                  <Tr key={key}>
                    <Td minWidth={80}>
                      {dailyAlertValue != null ? (
                        <span>
                          {dailyAlertValue.length > 100 ? (
                            <>
                              <Tooltip
                                label={dailyAlertValue}
                                fontSize="sm"
                              >
                                <span style={{ cursor: 'pointer' }}>
                                  {dailyAlertValue.slice(0, 100)}...{' '}
                                </span>
                              </Tooltip>
                            </>
                          ) : (
                            dailyAlertValue
                          )}
                          <br />
                          {alertExpirationValue && (
                            <Text fontWeight={'bold'}>
                              Alert Expiration:{' '}
                              {alertExpirationValue &&
                                `${alertExpirationValue}`}
                            </Text>
                          )}
                          <br />
                        </span>
                      ) : (
                        ''
                      )}
                      <Button
                        variant={'ghost'}
                        onClick={() =>
                          handleCellClick(
                            key,
                            'daily alert',
                            dailyAlertValue,
                            uuid,
                            alertExpirationValue
                          )
                        }
                        cursor="pointer"
                      >
                        <EditIcon w={4} h={4} color="blue.500" />
                      </Button>
                    </Td>
                    <Td minWidth={80}>
                      {officeNotesValue != null ? (
                        <span>
                          {officeNotesValue.length > 100 ? (
                            <>
                              <Tooltip
                                label={officeNotesValue}
                                fontSize="sm"
                              >
                                <span style={{ cursor: 'pointer' }}>
                                  {officeNotesValue.slice(0, 100)}...{' '}
                                </span>
                              </Tooltip>
                            </>
                          ) : (
                            officeNotesValue
                          )}
                          <br />
                          {notesExpirationValue && (
                            <Text fontWeight={'bold'}>
                              Notes Expiration:{''}
                              {notesExpirationValue &&
                                `${notesExpirationValue}`}
                            </Text>
                          )}
                          <br />
                        </span>
                      ) : (
                        ''
                      )}
                      <Button
                        variant={'ghost'}
                        onClick={() =>
                          handleCellClick(
                            key,
                            'office notes:',
                            officeNotesValue,
                            uuid,
                            notesExpirationValue
                          )
                        }
                        cursor="pointer"
                      >
                        <EditIcon w={4} h={4} color="blue.500" />
                      </Button>
                    </Td>
                    <Td>
                      {fileName ? (
                        <Button
                          onClick={() =>
                            handleDownload(fileName, key)
                          }
                          isLoading={
                            isDownloading && key === downloadKey
                          }
                          isDisabled={isDownloading}
                        >
                          <DownloadIcon cursor="pointer" />
                        </Button>
                      ) : (
                        'No file available'
                      )}
                    </Td>
                    {Object.keys(searchResults).map((columnName) => {
                      if (
                        columnName !== 'UUID' &&
                        columnName !== 'AlertExpiration' &&
                        columnName !== 'NotesExpiration' &&
                        columnName !== 'office notes:' &&
                        columnName !== 'daily alert' &&
                        columnName !== 'sheet' &&
                        columnName !== 'phone' &&
                        columnName !== 'URL' &&
                        columnName !== 'name' &&
                        columnName !== 'file' &&
                        columnName !== 'practice'
                      ) {
                        const value = searchResults[columnName][key];
                        rowData[columnName] = value;
                        return (
                          <Td
                            key={`${key}-${columnName}`}
                            minWidth={80}
                          >
                            <Box display="flex" alignItems="center">
                              {value != null ? (
                                value.length > 50 ? (
                                  <Tooltip
                                    label={value}
                                    fontSize="sm"
                                  >
                                    <span
                                      style={{ cursor: 'pointer' }}
                                    >
                                      {value.slice(0, 20)}...{' '}
                                    </span>
                                  </Tooltip>
                                ) : (
                                  value
                                )
                              ) : (
                                ''
                              )}
                              {columnName !== 'office name' &&
                                columnName !==
                                  'provider name (athena user id)' &&
                                columnName !== 'phone/fax' && (
                                  <Button variant={'ghost'}>
                                    <EditIcon
                                      ml={2}
                                      w={4}
                                      h={4}
                                      color="blue.500"
                                      cursor="pointer"
                                      onClick={() =>
                                        handleCellClick(
                                          key,
                                          columnName,
                                          value,
                                          uuid
                                        )
                                      }
                                    />
                                  </Button>
                                )}
                            </Box>
                          </Td>
                        );
                      }
                      return null;
                    })}
                  </Tr>
                );
              })}
            </Tbody>
            <Modal isOpen={isOpen} onClose={onClose}>
              <ModalOverlay />
              <ModalContent>
                <ModalHeader>Edit Value for {editColumn}</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                  {editColumn === 'office notes:' && (
                    <>
                      <Input
                        value={editValue?.toString()}
                        onChange={(e) => setEditValue(e.target.value)}
                        marginBottom={4}
                        placeholder="Office Notes Value"
                      />
                      <Input
                        value={expirationDate}
                        onChange={(e) =>
                          setExpirationDate(e.target.value)
                        }
                        type="date"
                        placeholder="Notes Expiration"
                      />
                    </>
                  )}
                  {editColumn === 'daily alert' && (
                    <>
                      <Input
                        value={editValue?.toString()}
                        onChange={(e) => setEditValue(e.target.value)}
                        marginBottom={4}
                        placeholder="Daily Alerts Value"
                      />
                      <Input
                        value={expirationDate}
                        onChange={(e) =>
                          setExpirationDate(e.target.value)
                        }
                        type="date"
                        placeholder="Alert Expiration"
                      />
                    </>
                  )}
                  {!(
                    editColumn === 'office notes:' ||
                    editColumn === 'daily alert'
                  ) && (
                    <Input
                      value={editValue?.toString()}
                      onChange={(e) => setEditValue(e.target.value)}
                      placeholder={`${editColumn} value`}
                    />
                  )}
                </ModalBody>
                <ModalFooter>
                  <Button
                    colorScheme="blue"
                    mr={3}
                    onClick={() => handleCellChange()}
                    isLoading={dataUpdating}
                    loadingText="Updating..."
                  >
                    Update
                  </Button>
                  <Button onClick={onClose}>Cancel</Button>
                </ModalFooter>
              </ModalContent>
            </Modal>
          </Table>
          <HStack spacing={4} justify="flex-start" margin={6}>
            <Text>
              Showing {indexOfFirstItem + 1} to{' '}
              {Math.min(
                indexOfLastItem,
                Object.keys(searchResults['phone/fax']).length
              )}{' '}
              of {Object.keys(searchResults['phone/fax']).length}{' '}
              entries
            </Text>
            <IconButton
              icon={<ChevronLeftIcon />}
              aria-label="Previous"
              onClick={() => setCurrentPage(currentPage - 1)}
              isDisabled={currentPage === 1}
            />
            <Text>{`Page ${currentPage}`}</Text>
            <IconButton
              icon={<ChevronRightIcon />}
              aria-label="Next"
              onClick={() => setCurrentPage(currentPage + 1)}
              isDisabled={
                indexOfLastItem >=
                Object.keys(searchResults['phone/fax']).length
              }
            />
          </HStack>
        </>
      ) : (
        <Box p={4} textAlign="center">
          <strong>No search results found.</strong>
        </Box>
      )}
    </Box>
  );
}
